import React, { useEffect, useMemo, useState } from 'react';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  getAllReservations,
  getReservationsCountSummary
} from '../../../../../ReservationModule/shared/services/data.service';
import {
  ReservationListUiModel,
  ReservationUiModel
} from '../../../../../shared/ui-models/reservation.ui-model';

type SummaryCardKey =
  | 'bookingsPrevMonth'
  | 'bookingsThisMonth'
  | 'reservationTeamBookings'
  | 'otaBookingsThisMonth'
  | 'websiteAppBookings'
  | 'checkInIn3Days'
  | 'checkOutIn3Days'
  | 'currentlyInHouse'
  | 'highDemandProject';

type SummaryCardConfig = {
  title: string;
};

type SummaryCardMetrics = {
  value: string;
  loading?: boolean;
};

type SummaryCardUiModel = {
  title: string;
  value: string;
  loading?: boolean;
  isLongValue?: boolean;
};

const summaryCardConfig: Record<SummaryCardKey, SummaryCardConfig> = {
  bookingsPrevMonth: {
    title: 'Previous Month'
  },
  bookingsThisMonth: {
    title: 'Current Month'
  },
  reservationTeamBookings: {
    title: 'Reservation Team'
  },
  otaBookingsThisMonth: {
    title: 'OTA’s Channels'
  },
  websiteAppBookings: {
    title: 'Website / APP'
  },
  checkInIn3Days: {
    title: 'Check-in in 3 days'
  },
  checkOutIn3Days: {
    title: 'Check-out in 3 days'
  },
  currentlyInHouse: {
    title: 'Currently in-house'
  },
  highDemandProject: {
    title: 'Highest Demand Project'
  }
};

const summaryCardOrder: SummaryCardKey[] = [
  'bookingsPrevMonth',
  'bookingsThisMonth',
  'reservationTeamBookings',
  'otaBookingsThisMonth',
  'websiteAppBookings',
  'checkInIn3Days',
  'checkOutIn3Days',
  'currentlyInHouse',
  'highDemandProject'
];

const formatCount = (value: number | null) => {
  if (!value) return '0';
  return value.toLocaleString();
};

const formatProjectValue = (name: string | null, count: number | null) => {
  if (!name || !count) return '0';
  return `${name} ${count.toLocaleString()} bookings `;
};

const getHighDemandSummary = (reservations: ReservationUiModel[]) => {
  const totals = reservations.reduce((acc: Record<string, number>, reservation) => {
    const key = reservation.propertyName || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  let topName: string | null = null;
  let topCount = 0;
  Object.entries(totals).forEach(([name, count]) => {
    if (count > topCount) {
      topName = name;
      topCount = count;
    }
  });

  return { name: topName, count: topCount };
};

const fetchHighDemandProjectSummary = async (query: Record<string, any>) => {
  const response = await getAllReservations(query, 1, 0);
  if (isErrorInfo(response)) {
    return { name: null, count: 0 };
  }

  const reservations = (response as ReservationListUiModel)?.reservationList || [];
  return getHighDemandSummary(reservations);
};

const activeReservationStatuses: ReservationStatus[] = Object.values(ReservationStatus).filter(
  (status) =>
    status !== ReservationStatus.CANCELED && status !== ReservationStatus.EXPIRED
) as ReservationStatus[];

const getStartOfYear = (year: number) => new Date(year, 0, 1);
const getEndOfYear = (year: number) => new Date(year, 11, 31, 23, 59, 59, 999);
const getStartOfMonth = (year: number, monthIndex: number) => new Date(year, monthIndex, 1);
const getEndOfMonth = (year: number, monthIndex: number) =>
  new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);

export const SummaryCards: React.FC = () => {
  const [cardMetrics, setCardMetrics] = useState<Record<SummaryCardKey, SummaryCardMetrics>>(() =>
    summaryCardOrder.reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          value: '0',
          loading: true
        }
      }),
      {} as Record<SummaryCardKey, SummaryCardMetrics>
    )
  );

  const dateRanges = useMemo(() => {
    const now = new Date();
    const last3DaysFrom = new Date(now);
    last3DaysFrom.setDate(now.getDate() - 3);
    const currentYear = now.getFullYear();
    const previousYear = currentYear - 1;
    const currentMonth = now.getMonth();
    const prevMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const twoMonthsAgoDate = new Date(currentYear, currentMonth - 2, 1);

    return {
      previousYear: {
        from: getStartOfYear(previousYear),
        to: getEndOfYear(previousYear)
      },
      yearBeforePrevious: {
        from: getStartOfYear(previousYear - 1),
        to: getEndOfYear(previousYear - 1)
      },
      currentYear: {
        from: getStartOfYear(currentYear),
        to: getEndOfYear(currentYear)
      },
      previousMonth: {
        from: getStartOfMonth(prevMonthDate.getFullYear(), prevMonthDate.getMonth()),
        to: getEndOfMonth(prevMonthDate.getFullYear(), prevMonthDate.getMonth())
      },
      twoMonthsAgo: {
        from: getStartOfMonth(twoMonthsAgoDate.getFullYear(), twoMonthsAgoDate.getMonth()),
        to: getEndOfMonth(twoMonthsAgoDate.getFullYear(), twoMonthsAgoDate.getMonth())
      },
      currentMonth: {
        from: getStartOfMonth(currentYear, currentMonth),
        to: getEndOfMonth(currentYear, currentMonth)
      }
    };
  }, []);

  const createdAtQuery = useMemo(
    () => (range: { from: Date; to: Date }) => ({
      createdAtFrom: range.from.getTime(),
      createdAtTo: range.to.getTime()
    }),
    []
  );

  useEffect(() => {
    const fetchCounts = async () => {
      const requestCount = async (query: Record<string, any>): Promise<number> => {
        const response = await getReservationsCountSummary(query, 1, 0);
        if (isErrorInfo(response)) {
          return 0;
        }
        const reservationResponse = response as ReservationListUiModel;
        return reservationResponse?.count ?? reservationResponse?.reservationList?.length ?? 0;
      };

      const thisMonthQuery = createdAtQuery(dateRanges.currentMonth);
      const previousMonthQuery = createdAtQuery(dateRanges.previousMonth);

      const otherCountsPromise = Promise.all([
        requestCount({ ...previousMonthQuery, statusQuery: activeReservationStatuses }),
        requestCount({ ...thisMonthQuery, statusQuery: activeReservationStatuses }),
        requestCount({ source: 'admin', ...thisMonthQuery, statusQuery: activeReservationStatuses }),
        requestCount({
          source: 'channel-manager',
          ...thisMonthQuery,
          statusQuery: activeReservationStatuses
        }),
        requestCount({ source: 'app', ...thisMonthQuery, statusQuery: activeReservationStatuses }),
        requestCount({ source: 'website', ...thisMonthQuery, statusQuery: activeReservationStatuses }),
        requestCount({
          range: 'upcomingCheckIns',
          statusQuery: [
            ReservationStatus.CHECKED_IN,
            ReservationStatus.CHECKED_OUT,
            ReservationStatus.CONFIRMATION_PENDING,
            ReservationStatus.IN_HOUSE,
            ReservationStatus.CHECKED_IN_STARTED
          ]
        }),
        requestCount({
          range: 'upcomingDepatures',
          statusQuery: [
            ReservationStatus.CHECKED_IN,
            ReservationStatus.CHECKED_OUT,
            ReservationStatus.CONFIRMATION_PENDING,
            ReservationStatus.IN_HOUSE,
            ReservationStatus.CHECKED_IN_STARTED
          ]
        }),
        requestCount({
          range: 'hosting',
          status: ReservationStatus.IN_HOUSE
        })
      ]);

      const [
        bookingsPrevMonth,
        bookingsThisMonth,
        reservationTeamBookings,
        otaBookingsThisMonth,
        appBookings,
        websiteBookings,
        checkInIn3Days,
        checkOutIn3Days,
        currentlyInHouse
      ] = await otherCountsPromise;

      const websiteAppBookingsTotal = appBookings + websiteBookings;

      setCardMetrics((prev) => ({
        ...prev,
        bookingsPrevMonth: {
          value: formatCount(bookingsPrevMonth),
          loading: false
        },
        bookingsThisMonth: {
          value: formatCount(bookingsThisMonth),
          loading: false
        },
        reservationTeamBookings: {
          value: formatCount(reservationTeamBookings),
          loading: false
        },
        otaBookingsThisMonth: {
          value: formatCount(otaBookingsThisMonth),
          loading: false
        },
        websiteAppBookings: {
          value: formatCount(websiteAppBookingsTotal),
          loading: false
        },
        checkInIn3Days: {
          value: formatCount(checkInIn3Days),
          loading: false
        },
        checkOutIn3Days: {
          value: formatCount(checkOutIn3Days),
          loading: false
        },
        currentlyInHouse: {
          value: formatCount(currentlyInHouse),
          loading: false
        }
      }));
    };

    fetchCounts();

    const fetchHighDemandProject = async () => {
      const thisMonthQuery = createdAtQuery(dateRanges.currentMonth);
      const highDemandSummary = await fetchHighDemandProjectSummary({ ...thisMonthQuery });

      setCardMetrics((prev) => ({
        ...prev,
        highDemandProject: {
          value: formatProjectValue(highDemandSummary.name, highDemandSummary.count),
          loading: false
        }
      }));
    };

    fetchHighDemandProject();
  }, [createdAtQuery, dateRanges]);

  const summaryCards: SummaryCardUiModel[] = summaryCardOrder.map((key) => {
    const title = summaryCardConfig[key].title;
    const value = cardMetrics[key].value;

    return {
      title,
      value,
      loading: cardMetrics[key].loading,
      isLongValue: key === 'highDemandProject'
    };
  });

  return (
    <div className="mb-12 w-full max-w-screen-2xl mx-auto px-2 md:px-0">
      <section className="w-full">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:flex lg:justify-center lg:items-center lg:flex-wrap">
          {summaryCards.map((card) => (
            <div
              key={card.title}
              className="group relative overflow-hidden rounded-2xl border  bg-gradient-to-br from-white to-slate-50/60 p-6 shadow-lg transition hover:-translate-y-0.5 border-primary-300/80 hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] lg:basis-[240px]"
            >
              <div className="pointer-events-none absolute inset-0 opacity-100">
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primary-500/10 blur-2xl" />
                <div className="absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-primary-500/10 blur-2xl" />
              </div>

              {card.loading ? (
                <>
                  <div className="h-5 w-40 animate-pulse rounded bg-slate-200/70" />
                  <div className="mt-4 h-10 w-24 animate-pulse rounded bg-slate-200/70" />
                </>
              ) : (
                <>
                  <div className="text-[15px] font-semibold tracking-tight text-neutral-200">
                    {card.title}
                  </div>
                  <div
                    className={
                      card.isLongValue
                        ? 'mt-3 break-words text-2xl font-gilroy-bold font-medium leading-snug tracking-tight text-primary-700/90'
                        : 'mt-3 text-4xl font-gilroy-medium font-medium tracking-tight text-primary-700/90'
                    }
                  >
                    {card.value}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
